// src/components/chat/ChatComposerV2.jsx
//
// Chat v2 message composer. Handles text + image attachments.
//
// Behavior:
//   - Auto-grow textarea (1 line min, 6 line max)
//   - Enter to send, Shift+Enter for new line (desktop)
//   - 📎 attach button picks an image; 📷 visible on mobile
//     opens the camera directly via capture="environment"
//   - Selected image shows as a preview with remove (✕) button
//     above the textarea, with caption optional
//   - Send button is enabled when there's either text OR an image
//   - Real-time progress bar during compress/upload (Phase 5)
//   - On error: text restored, image kept selected so retry works
//   - Disables itself while a send is in-flight

import React, { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import {
  PaperAirplaneIcon,
  ArrowPathIcon,
  PaperClipIcon,
  CameraIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { sendChatMessage } from '@/services/chatSend'
import { sendChatImage, sendChatPdf } from '@/services/chatAttachments'
import { DocumentTextIcon } from '@heroicons/react/24/outline'

const HEAD_FONT = "'Barlow Condensed', sans-serif"
const MAX_LEN = 5000
// PDFs cap at 10MB — coaches send registration forms, waivers,
// and schedules; anything bigger likely needs to be slimmed down
// or hosted elsewhere. Storage rules enforce the same ceiling.
const PDF_MAX_BYTES = 10 * 1024 * 1024

// Detect mobile so we can show a separate camera button. Not
// perfect (some tablets get treated as desktop) but the camera
// button is additive — users can still attach via the gallery
// button if the camera one isn't shown.
function isMobile() {
  if (typeof navigator === 'undefined') return false
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
}

export default function ChatComposerV2({
  chatId,
  senderId,
  senderName,
  senderPhotoURL,
  placeholder = 'Type a message...',
  disabled = false,
  // Optional context props for team chats. When chatType ===
  // 'team_announcement' AND chatMode === 'broadcast' AND
  // teamRoleOnChat === 'parent', the composer renders nothing — the
  // parent should not see a text box at all. ChatRoomV2 already gates
  // on this (canPost goes false), so this is belt-and-suspenders to
  // make the component safe to drop into any future surface that
  // forgets the gate.
  chatType = null,
  chatMode = 'open',
  teamRoleOnChat = null,
}) {
  // Defensive hide: never render the composer for a parent looking at
  // a broadcast-only team announcement chat. See doc above the prop
  // signature for context.
  if (
    chatType === 'team_announcement' &&
    chatMode === 'broadcast' &&
    teamRoleOnChat === 'parent'
  ) {
    return null
  }

  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [imageFile, setImageFile] = useState(null)
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null)
  // PDF attachments are mutually exclusive with images — pick one
  // attachment per message to keep the renderer simple.
  const [pdfFile, setPdfFile] = useState(null)
  const [progress, setProgress] = useState(0)
  const textareaRef = useRef(null)
  const fileInputRef = useRef(null)
  const cameraInputRef = useRef(null)
  const [showCamera] = useState(isMobile())

  // Auto-grow the textarea up to ~6 lines, then let it scroll
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    const max = 6 * 24 // ~6 lines at 24px line-height
    el.style.height = Math.min(el.scrollHeight, max) + 'px'
  }, [text])

  // Build a preview URL when an image is selected; revoke on
  // change/unmount so we don't leak object URLs.
  useEffect(() => {
    if (!imageFile) {
      setImagePreviewUrl(null)
      return
    }
    const url = URL.createObjectURL(imageFile)
    setImagePreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [imageFile])

  const trimmed = text.trim()
  const overLimit = trimmed.length > MAX_LEN
  const showCounter = trimmed.length > MAX_LEN - 500
  const hasContent = imageFile || pdfFile || (trimmed.length > 0 && trimmed.length <= MAX_LEN)
  const canSend = !sending && !disabled && hasContent && !overLimit

  const pickFile = (refObj) => {
    if (sending || disabled) return
    refObj.current?.click()
  }

  const handleFileChange = (e) => {
    const f = e.target.files?.[0]
    e.target.value = '' // reset so picking the same file again still fires onChange
    if (!f) return
    // Branch on MIME — coaches can pick images OR PDFs from the
    // same picker. Anything else is rejected with a toast.
    const isPdf = f.type === 'application/pdf'
    const isImage = !!f.type?.startsWith('image/')
    if (!isPdf && !isImage) {
      toast.error('Only images and PDFs are supported.')
      return
    }
    if (isPdf) {
      if (f.size > PDF_MAX_BYTES) {
        toast.error('PDFs must be under 10 MB.')
        return
      }
      setImageFile(null) // attachments are mutually exclusive
      setPdfFile(f)
      return
    }
    if (f.size > 30 * 1024 * 1024) {
      toast.error('That image is over 30MB. Try a smaller one.')
      return
    }
    setPdfFile(null)
    setImageFile(f)
  }

  const removeImage = () => {
    setImageFile(null)
    setProgress(0)
  }

  const removePdf = () => {
    setPdfFile(null)
    setProgress(0)
  }

  // Format a byte count into a friendly "1.2 MB" / "456 KB" string
  // for the attachment chip + (later) the message bubble.
  const fmtBytes = (n) => {
    if (!n && n !== 0) return ''
    if (n < 1024) return `${n} B`
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`
    return `${(n / (1024 * 1024)).toFixed(1)} MB`
  }

  const handleSend = async () => {
    if (!canSend) return
    setSending(true)
    setProgress(0)

    const toSendText = trimmed
    const toSendImage = imageFile
    const toSendPdf = pdfFile

    // Optimistic clear so the composer feels responsive. We
    // hold onto the values so we can restore on failure.
    setText('')
    setImageFile(null)
    setPdfFile(null)

    try {
      if (toSendImage) {
        await sendChatImage({
          chatId,
          senderId,
          senderName,
          senderPhotoURL,
          file: toSendImage,
          caption: toSendText,
          onProgress: (p) => setProgress(p),
        })
      } else if (toSendPdf) {
        await sendChatPdf({
          chatId,
          senderId,
          senderName,
          senderPhotoURL,
          file: toSendPdf,
          caption: toSendText,
          onProgress: (p) => setProgress(p),
        })
      } else {
        await sendChatMessage({
          chatId,
          senderId,
          senderName,
          senderPhotoURL,
          text: toSendText,
        })
      }
    } catch (err) {
      console.error('[ChatComposerV2] send failed:', err?.code, err?.message)
      // Restore so they can retry without re-typing/re-picking
      setText(toSendText)
      if (toSendImage) setImageFile(toSendImage)
      if (toSendPdf) setPdfFile(toSendPdf)
      toast.error(err?.message || 'Could not send. Try again.')
    } finally {
      setSending(false)
      setProgress(0)
    }
  }

  const handleKeyDown = (e) => {
    // Enter sends only when there's no image-attach pending; with
    // an image attached, Enter still sends but the user usually
    // wants to type a caption first. The text+image send path
    // below handles either case.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <div className="bg-white border-t border-zinc-200 px-3 sm:px-4 pt-2.5 pb-[calc(0.625rem+64px+env(safe-area-inset-bottom))] md:pb-2.5 sticky bottom-0">
      <div className="max-w-2xl mx-auto">

        {/* Image preview row */}
        {imagePreviewUrl && (
          <div className="mb-2 inline-flex relative">
            <img
              src={imagePreviewUrl}
              alt="Selected attachment"
              className="h-24 max-w-[200px] rounded-xl object-cover ring-1 ring-zinc-200"
            />
            <button
              type="button"
              onClick={removeImage}
              disabled={sending}
              aria-label="Remove image"
              className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-[#0d1f14] text-white shadow-md flex items-center justify-center hover:bg-[#1a2e1f] transition-colors disabled:opacity-50"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
            {sending && progress > 0 && progress < 100 && (
              <div className="absolute inset-x-0 bottom-0 h-1 bg-black/30 rounded-b-xl overflow-hidden">
                <div
                  className="h-full bg-[#b5f03d] transition-all duration-150"
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}
          </div>
        )}

        {/* PDF preview chip — mirrors the image preview pattern but
            shows a doc icon + filename + size since there's no visual
            thumbnail to render. Lime/pitch palette to match the
            design system. */}
        {pdfFile && (
          <div className="mb-2 inline-flex relative">
            <div className="flex items-center gap-2.5 max-w-[260px] rounded-xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 px-3 py-2">
              <DocumentTextIcon className="h-6 w-6 text-[#5a8a1f] flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-xs font-bold text-[#0d1f14] truncate">
                  {pdfFile.name}
                </p>
                <p className="text-[10px] text-zinc-500">
                  PDF &middot; {fmtBytes(pdfFile.size)}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={removePdf}
              disabled={sending}
              aria-label="Remove PDF"
              className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-[#0d1f14] text-white shadow-md flex items-center justify-center hover:bg-[#1a2e1f] transition-colors disabled:opacity-50"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
            {sending && progress > 0 && progress < 100 && (
              <div className="absolute inset-x-0 bottom-0 h-1 bg-black/30 rounded-b-xl overflow-hidden">
                <div
                  className="h-full bg-[#b5f03d] transition-all duration-150"
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}
          </div>
        )}

        <div className="flex items-end gap-1.5 sm:gap-2">

          {/* Attach (gallery) button */}
          <button
            type="button"
            onClick={() => pickFile(fileInputRef)}
            disabled={sending || disabled}
            aria-label="Attach image or PDF"
            title="Attach image or PDF"
            className="inline-flex items-center justify-center h-11 w-11 rounded-2xl text-zinc-500 hover:text-[#5a8a1f] hover:bg-[#b5f03d]/10 transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PaperClipIcon className="h-5 w-5" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            // Accept both images and PDFs — PDFs cover registration
            // forms, waivers, and league schedules that coaches
            // commonly need to share inline with their teams.
            accept="image/*,application/pdf"
            onChange={handleFileChange}
            className="hidden"
          />

          {/* Camera button (mobile only) */}
          {showCamera && (
            <>
              <button
                type="button"
                onClick={() => pickFile(cameraInputRef)}
                disabled={sending || disabled}
                aria-label="Take photo"
                title="Take photo"
                className="inline-flex items-center justify-center h-11 w-11 rounded-2xl text-zinc-500 hover:text-[#5a8a1f] hover:bg-[#b5f03d]/10 transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CameraIcon className="h-5 w-5" />
              </button>
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileChange}
                className="hidden"
              />
            </>
          )}

          {/* Textarea */}
          <div className="flex-1 min-w-0 relative">
            <textarea
              ref={textareaRef}
              rows={1}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={(imageFile || pdfFile) ? 'Add a caption (optional)...' : placeholder}
              disabled={disabled || sending}
              maxLength={MAX_LEN + 100}
              className="w-full resize-none rounded-2xl border border-zinc-200 bg-white px-4 py-2.5 text-sm text-[#0d1f14] placeholder:text-zinc-400 focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ maxHeight: '144px', overflowY: text.split('\n').length > 6 ? 'auto' : 'hidden' }}
            />
            {showCounter && (
              <p
                className={[
                  'absolute right-3 bottom-1 text-[10px] font-bold pointer-events-none',
                  overLimit ? 'text-red-500' : 'text-zinc-400',
                ].join(' ')}
              >
                {trimmed.length} / {MAX_LEN}
              </p>
            )}
          </div>

          {/* Send button */}
          <button
            type="button"
            onClick={handleSend}
            disabled={!canSend}
            aria-label="Send"
            className={[
              'inline-flex items-center justify-center h-11 w-11 rounded-2xl transition-all flex-shrink-0',
              canSend
                ? 'bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] active:scale-[0.95]'
                : 'bg-zinc-100 text-zinc-300 cursor-not-allowed',
            ].join(' ')}
            style={{ fontFamily: HEAD_FONT }}
          >
            {sending
              ? <ArrowPathIcon className="h-5 w-5 animate-spin" />
              : <PaperAirplaneIcon className="h-5 w-5" />}
          </button>
        </div>

        {/* Hint line */}
        <p className="text-[10px] text-zinc-400 mt-1.5 px-1 hidden sm:block">
          Enter to send, Shift+Enter for new line. Attach images (up to 30MB, compressed automatically) or PDFs (up to 10MB).
        </p>
      </div>
    </div>
  )
}
